import SwinglyChatData from '../data/SwinglyChatData';

class ChatApp {
    constructor() {
        this.dataProvider = new SwinglyChatData();
        this.groupsList = [];
        this.contactsList = [];
        this.talkSession = null;
    }

    // Initialize data
    async initializeData() {
        this.wpUser = await this.dataProvider.fetchCurrentUser();
        this.contactsList = await this.dataProvider.fetchUsers(wpUser?.ID);
        this.groupsList = await this.dataProvider.fetchCurrentUserGroups();
    }

    // Initialize TalkJS
    async initialize() {
        // Add mobile-specific styles
        const style = document.createElement('style');
        // style.textContent = `
           
        // `;
        document.head.appendChild(style);

        // Wait for TalkJS to be fully loaded
        if (typeof Talk === 'undefined') {
            console.error('TalkJS not loaded');
            return;
        }

        // Wait for data to be loaded first
        await this.initializeData();

        // Use the Talk.ready promise to ensure TalkJS is fully initialized
        return Talk.ready.then(() => {
            // console.log("TalkJS is ready");
            
            // Create user "me"
            this.me = new Talk.User({
                id: this.wpUser.id,
                name: this.wpUser.name,
                photoUrl: this.wpUser.photoUrl,
                role: 'default',
                info: 'Available',
            });

            // Start TalkJS Session
            this.talkSession = new Talk.Session({
                appId: 't1ongDQ5',
                me: this.me
            });

            // Create group conversations
            this.groupConversations = this.createGroupConversations(this.me);
            
            // Create one-on-one conversations
            this.oneOnOneConversations = this.createOneOnOneConversations(this.me);

            // Store me reference for later use
            // this.me = me;
            
            // Start presence simulation
            this.updateOnlineStatus();
        }).catch((error) => {
            console.error('TalkJS initialization failed:', error);
        });
    }

    // Create group conversations
    createGroupConversations(me) {
        return this.groupsList.map(group => {
            
            // First create a conversation ID
            const conversationId = `group_${group.id}`;
            
            // Create the conversation
            const conversation = this.talkSession.getOrCreateConversation(conversationId);
            
            // Set the group metadata
            conversation.setAttributes({
                subject: group.name,
                photoUrl: null,
                custom: {
                    description: group.description
                }
            });

            // Add all participants
            conversation.setParticipant(me);
            this.contactsList.forEach(contact => {
                const groupMember = new Talk.User({
                    id: contact.id.toString(),
                    name: contact.name,
                    photoUrl: contact.photoUrl,
                    role: 'default'
                });
                conversation.setParticipant(groupMember);
            });

            return conversation;
        });
    }

    // Create one-on-one conversations
    createOneOnOneConversations(me) {
        return this.contactsList.map((user) => {
            
            const talkUser = new Talk.User({
                id: user.id.toString(),
                name: user.name,
                photoUrl: user.photoUrl,
                role: 'default'
            });

            const conversation = this.talkSession.getOrCreateConversation(Talk.oneOnOneId(me, talkUser));
            conversation.setParticipant(me);
            conversation.setParticipant(talkUser);
            return conversation;
        });
    }

    // Method to set up chat handlers (to be called after UI is rendered)
    setupChatHandlers() {
        // Add click handlers to groups
        document.querySelectorAll('.contacts-wrapper-groups .contact-item').forEach((groupElement, index) => {
            groupElement.addEventListener('click', () => {
                const group = this.groupsList[index];
                const conversation = this.groupConversations[index];
                const existingWindow = document.getElementById(`chat-window-${group.id}`);
                
                if (!existingWindow) {
                    const chatbox = this.talkSession.createChatbox();
                    const chatContent = this.createChatWindow(group, conversation, chatbox);
                    chatbox.select(conversation);
                    chatbox.mount(chatContent);
                } else {
                    existingWindow.classList.remove('minimized');
                    const minimizeBtn = existingWindow.querySelector('.actions button');
                    minimizeBtn.textContent = '−';
                }
            });
        });

        // Add click handlers to contacts
        document.querySelectorAll('.contacts-wrapper-members .contact-item').forEach((contactElement, index) => {
            contactElement.addEventListener('click', () => {
                const contact = this.contactsList[index];
                const conversation = this.oneOnOneConversations[index];
                const existingWindow = document.getElementById(`chat-window-${contact.id}`);
                
                if (!existingWindow) {
                    const chatbox = this.talkSession.createChatbox();
                    const chatContent = this.createChatWindow(contact, conversation, chatbox);
                    chatbox.select(conversation);
                    chatbox.mount(chatContent);
                } else {
                    existingWindow.classList.remove('minimized');
                    const minimizeBtn = existingWindow.querySelector('.actions button');
                    minimizeBtn.textContent = '−';
                }
            });
        });
    }

    // Function to create a new chat window
    createChatWindow(contact, conversation, chatbox) {
        const chatTabs = document.getElementById('chat-tabs');
        
        // Check if we're on mobile
        const isMobile = window.innerWidth <= 768;
        
        // If on mobile, remove any existing chat windows
        if (isMobile) {
            const existingWindows = document.querySelectorAll('.chat-window');
            existingWindows.forEach(window => {
                window.remove();
            });
            // Hide contacts list
            const contactsList = document.querySelector('.contacts');
            if (contactsList) {
                contactsList.style.display = 'none';
            }
        }
        
        // Create chat window container
        const chatWindow = document.createElement('div');
        chatWindow.classList.add('chat-window');
        chatWindow.id = `chat-window-${contact.id}`;
        
        // Add mobile-specific class
        if (isMobile) {
            chatWindow.classList.add('mobile-chat');
        }

        // Create chat header
        const header = document.createElement('div');
        header.classList.add('chat-header');
        
        const title = document.createElement('div');
        title.classList.add('title');
        title.textContent = contact.name;

        // Add status indicator to chat header
        const statusIndicator = document.createElement('div');
        statusIndicator.classList.add('status-indicator');
        statusIndicator.id = `chat-status-${contact.id}`;
        title.appendChild(statusIndicator);

        const actions = document.createElement('div');
        actions.classList.add('actions');

        const minimizeBtn = document.createElement('button');
        minimizeBtn.textContent = '−';
        minimizeBtn.onclick = () => {
            chatWindow.classList.toggle('minimized');
            minimizeBtn.textContent = chatWindow.classList.contains('minimized') ? '+' : '−';
        };

        const closeBtn = document.createElement('button');
        closeBtn.textContent = '×';
        closeBtn.onclick = () => {
            chatWindow.remove();
            chatbox.select(null);
        };

        actions.appendChild(minimizeBtn);
        actions.appendChild(closeBtn);
        header.appendChild(title);
        header.appendChild(actions);

        // Create chat content container
        const chatContent = document.createElement('div');
        chatContent.classList.add('chat-content');
        chatContent.id = `talkjs-container-${contact.id}`;

        chatWindow.appendChild(header);
        chatWindow.appendChild(chatContent);
        chatTabs.appendChild(chatWindow);

        return chatContent;
    }

    // Function to update online status indicators
    updateOnlineStatus() {
        setInterval(() => {
            this.contactsList.forEach(user => {
                const isOnline = Math.random() < 0.5;
                const statusIndicator = document.getElementById(`status-${user.id}`);
                const chatStatusIndicator = document.getElementById(`chat-status-${user.id}`);
                
                if (statusIndicator) {
                    statusIndicator.classList.toggle('online', isOnline);
                }
                if (chatStatusIndicator) {
                    chatStatusIndicator.classList.toggle('online', isOnline);
                }
            });
        }, 5000);
    }
}

export default ChatApp; 